'use client';

import type {
  CertifiedViewExperimentData,
  CertifiedViewFact
} from '@/analytics/models/cventAnalytics/CertifiedViewFact';
import { useInView } from 'react-intersection-observer';
import { useState } from 'react';
import useAnalytics from '@/hooks/useAnalytics';

const onEntry = (payload: Omit<CertifiedViewFact, 'event'>, callback: (payload: CertifiedViewFact) => void) => {
  const data = {
    ...payload,
    event: 'entry'
  };
  callback(data);
};

const onExit = (payload: Omit<CertifiedViewFact, 'event'>, callback: (payload: CertifiedViewFact) => void) => {
  const data = {
    ...payload,
    event: 'exit'
  };
  callback(data);
};

const onCertifiedView = (payload: Omit<CertifiedViewFact, 'event'>, callback: (payload: CertifiedViewFact) => void) => {
  const data = {
    ...payload,
    event: 'view'
  };
  callback(data);
};

export function Observer({
  children,
  payload,
  certifiedViewExperimentData
}: {
  children: JSX.Element;
  payload: Omit<CertifiedViewFact, 'event'>;
  certifiedViewExperimentData: CertifiedViewExperimentData;
}) {
  const [lastView, setLastView] = useState(false);
  const [viewTimer, setViewTimer] = useState<NodeJS.Timeout>();
  const analytics = useAnalytics();
  const { threshold, waitTime, disabled } = certifiedViewExperimentData;
  const callback = data => analytics.reportFact<CertifiedViewFact>(data);
  const { ref } = useInView({
    onChange: inView => {
      // element entered
      if (inView && !lastView) {
        onEntry(payload, callback);

        // start a timer for certified view
        const timer = setTimeout(() => {
          onCertifiedView(payload, callback);
        }, waitTime);
        setViewTimer(timer);
        setLastView(true);

        // element exited
      } else if (!inView && lastView) {
        onExit(payload, callback);
        clearTimeout(viewTimer);
        setLastView(false);
      }
    },
    threshold,
    skip: disabled,
    // taking care of top sticky stuff (header + filters)
    rootMargin: '-272px 0px 0px 0px'
  });

  return (
    <div className="w-full" ref={ref}>
      {children}
    </div>
  );
}

export default Observer;
